import React, { useState } from 'react';
import { Sankey, Tooltip, Layer, Rectangle, ResponsiveContainer } from 'recharts';
import { Box, Typography, CircularProgress, Button, Tooltip as MuiTooltip, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, Divider, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { getComponentDescription } from '../utils/SankeyComponentDescriptions';

// Currency symbols for display
const currencySymbols = {
  USD: '$',
  JPY: '¥',
  EUR: '€',
  GBP: '£',
  CNY: '¥',
  TWD: 'NT$',
  KRW: '₩',
  INR: '₹',
  BRL: 'R$',
  CAD: 'C$',
  AUD: 'A$',
  CHF: 'Fr',
  RMB: '¥',
};

const getCurrencySymbol = (curr) => currencySymbols[curr] || curr || '$';

// Helper function to format numbers to 3 significant figures
const formatToSigFigs = (value) => {
  if (value === undefined || value === null || isNaN(value)) return '0';
  if (value === 0) return '0';
  
  // Convert to 3 significant figures
  return Number.parseFloat(value).toPrecision(3);
};

// Format large numbers with si units for display
const formatDisplayValue = (value, currencySymbol = '$') => {
  // Handle undefined, null, NaN or zero values
  if (value === undefined || value === null || isNaN(value)) {
    return `${currencySymbol}0`;
  }
  if (value === 0) {
    return `${currencySymbol}0`;
  }
  
  const absValue = Math.abs(value);
  let formatted;
  
  if (absValue >= 1000000000) {
    formatted = `${currencySymbol}${formatToSigFigs(value / 1000000000)}B`;
  } else if (absValue >= 1000000) {
    formatted = `${currencySymbol}${formatToSigFigs(value / 1000000)}M`;
  } else if (absValue >= 1000) {
    formatted = `${currencySymbol}${formatToSigFigs(value / 1000)}K`;
  } else {
    formatted = `${currencySymbol}${formatToSigFigs(value)}`;
  }
  
  return formatted;
};

const getCashFlowSubcomponentColor = (subComponent, theme, fallbackColor) => {
  if (typeof subComponent !== 'string') return fallbackColor;
  const text = subComponent.toLowerCase();

  // Outflows: show red. Includes buybacks explicitly.
  const outflowIndicators = [
    'buyback',
    'repayment',
    'paydown',
    'dividend',
    'payment',
    'capex',
    'capital expenditures',
    'purchase',
    'acquisition',
    'investment in securities',
    'lease payments',
    'bond redemptions',
  ];

  // Inflows/non-cash add-backs: show green.
  const inflowIndicators = [
    'issuance',
    'sales',
    'disposals',
    'divestiture',
    'equity financing',
    'debt financing',
    'add back',
  ];

  if (outflowIndicators.some((k) => text.includes(k))) {
    return theme.palette.error.main;
  }
  if (inflowIndicators.some((k) => text.includes(k))) {
    return theme.palette.success.main;
  }
  return fallbackColor;
};

const getIncomeSubcomponentColor = (subComponent, theme, fallbackColor) => {
  if (typeof subComponent !== 'string') return fallbackColor;
  const text = subComponent.toLowerCase();

  // Explanatory lines should follow the parent section polarity color.
  const explanatoryPrefixes = [
    'calculated from:',
    'indicates',
    'key metric',
    'excludes',
    'foundation for',
    'determines',
    'should reconcile',
  ];

  if (explanatoryPrefixes.some((k) => text.startsWith(k))) {
    return fallbackColor;
  }

  const expenseIndicators = [
    'cost',
    'expense',
    'cogs',
    'sg&a',
    'research',
    'marketing',
    'salaries',
    'depreciation',
    'amortization',
    'tax',
    'interest',
    'overhead',
    'impairment',
  ];

  const incomeIndicators = [
    'revenue',
    'sales',
    'gross profit',
    'operating income',
    'net income',
    'licensing',
    'subscription',
    'other operating revenue',
  ];

  if (expenseIndicators.some((k) => text.includes(k))) {
    return theme.palette.error.main;
  }
  if (incomeIndicators.some((k) => text.includes(k))) {
    return theme.palette.success.main;
  }
  return fallbackColor;
};

// Function to truncate text if it's too long
const truncateText = (text, maxLength) => {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

// Function to calculate optimal label positioning and truncation
const calculateLabelProperties = (x, y, width, containerWidth, name, isPriorityLabel) => {
  const isLeftSide = x < containerWidth / 2;
  const isNearLeftEdge = x < containerWidth * 0.2;
  const isNearRightEdge = x + width > containerWidth * 0.8;
  
  // Default properties
  let nodeX = isLeftSide ? x + width + 10 : x - 10;
  let textAnchor = isLeftSide ? 'start' : 'end';
  let maxNameLength = 25; // Default length
  
  // Adjust for very edge cases
  if (isNearLeftEdge) {
    nodeX = x + width + 5;
    textAnchor = 'start';
    maxNameLength = 20;
  } else if (isNearRightEdge) {
    nodeX = x - 5;
    textAnchor = 'end';
    maxNameLength = 20;
  }
  
  // For priority labels, use a longer maximum length
  if (isPriorityLabel) {
    maxNameLength = 35; // Even longer for important labels
  }
  
  // Calculate if we need to truncate
  const needsTruncation = name.length > maxNameLength;
  const displayName = needsTruncation ? truncateText(name, maxNameLength) : name;
  
  return {
    nodeX,
    textAnchor,
    displayName,
    needsTruncation
  };
};

const CustomNode = ({ x, y, width, height, index, payload, containerWidth, labelColor, currencySymbol, useSignedCashFlowColors = false, nodeValueLookup = {} }) => {
  if (!payload) return null;
  
  // Define explicit color mapping based on node names
  const getNodeColor = (nodeName, nodeValue) => {
    if (useSignedCashFlowColors && Number.isFinite(nodeValue)) {
      return nodeValue >= 0 ? '#16a34a' : '#dc2626';
    }

    if (typeof nodeName !== 'string') {
      return payload.color || payload.fill || '#8884d8';
    }

    // Check more specific names first to avoid conflicts
    if (nodeName.includes('Cost of Revenue')) return 'red';
    if (nodeName.includes('Gross Profit')) return 'green';
    if (nodeName.includes('Operating Expenses')) return 'red';
    if (nodeName.includes('Operating Loss')) return 'red';
    if (nodeName.includes('Operating Income')) return 'green';
    if (nodeName.includes('DEBT') || nodeName.includes('Debt')) return 'red';
    if (nodeName.includes('Revenue')) return 'blue'; // Check this last since it's more general
    
    // Fallback to payload color or default
    return payload.color || payload.fill || '#8884d8';
  };
  
  // Recharts may provide node data directly on payload or nested at payload.payload.
  // Prefer original signed values from either shape before falling back to computed values.
  const rawNodeData = payload?.payload && typeof payload.payload === 'object'
    ? payload.payload
    : payload;
  const nodeName = typeof rawNodeData?.name === 'string'
    ? rawNodeData.name
    : (typeof payload?.name === 'string' ? payload.name : 'Node');
  const lookupValue = nodeValueLookup[nodeName];
  const nodeDisplaySource = lookupValue?.signedValue ?? rawNodeData?.signedValue ?? rawNodeData?.value ?? payload?.value;
  const displayValue = lookupValue?.formattedValue || rawNodeData?.formattedValue || payload?.formattedValue || formatDisplayValue(nodeDisplaySource, currencySymbol);
  
  // Get container width from props or use a default value
  const effectiveContainerWidth = containerWidth || 1000;
  
  // Calculate label properties - pass priorityLabel flag if exists
  const { nodeX, textAnchor, displayName, needsTruncation } = 
    calculateLabelProperties(x, y, width, effectiveContainerWidth, nodeName, payload.priorityLabel);
  
  // Create a text element for the label
  const nodeLabel = (
    <text
      textAnchor={textAnchor}
      x={nodeX}
      y={y + height / 2}
      dy={4} // Center vertically
      fontSize="12"
      fontFamily="Arial, sans-serif"
      fill={labelColor}
    >
      {`${displayName} ${displayValue}`}
    </text>
  );
  
  // Get the node color using our explicit mapping
  const nodeColor = getNodeColor(nodeName, Number(nodeDisplaySource));
  
  return (
    <Layer>
      <Rectangle
        x={x}
        y={y}
        width={width}
        height={height}
        fill={nodeColor}
        fillOpacity="0.8"
        stroke={nodeColor}
        strokeWidth="1"
      />
      
      {needsTruncation ? (
        <foreignObject
          x={textAnchor === 'start' ? nodeX - 5 : nodeX - 200}
          y={y + height / 2 - 10}
          width={200}
          height={20}
        >
            <MuiTooltip title={`${nodeName} ${displayValue}`} placement="top">
            <span style={{ fontSize: '12px' }}>
              {nodeLabel}
            </span>
          </MuiTooltip>
        </foreignObject>
      ) : nodeLabel}
    </Layer>
  );
};

const CustomLink = ({ sourceX, targetX, sourceY, targetY, sourceControlX, targetControlX, linkWidth, index, payload, linkStroke }) => {
  return (
    <Layer>
      <path
        d={`
          M${sourceX},${sourceY}
          C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}
        `}
        stroke={linkStroke}
        strokeWidth={linkWidth}
        fill="none"
        strokeOpacity="0.35"
      />
    </Layer>
  );
};

const ReactSankeyDiagram = ({ data, title, loading = false, onRefresh, currency = 'USD', statementType = 'income' }) => {
  const theme = useTheme();
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const isDarkMode = theme.palette.mode === 'dark';
  const labelColor = isDarkMode ? '#e2e8f0' : '#1f2937';
  const linkStroke = isDarkMode ? '#94a3b8' : '#64748b';
  
  // Get currency symbol - use currency from data if available, otherwise use prop
  const currencySymbol = getCurrencySymbol(currency);

  const baseNodes = Array.isArray(data?.nodes)
    ? data.nodes.filter((node) => node && typeof node.name === 'string' && node.name.length > 0)
    : [];

  const normalizedNodes = baseNodes;
  const endpointAliases = {};

  const renderNodes = normalizedNodes.map((node) => {
    const signedValue = Number(node?.value);
    return {
      ...node,
      signedValue: Number.isFinite(signedValue) ? signedValue : undefined,
      formattedValue: Number.isFinite(signedValue)
        ? formatDisplayValue(signedValue, currencySymbol)
        : undefined,
    };
  });

  const nodeNameToIndex = renderNodes.reduce((acc, node, index) => {
    acc[node.name] = index;
    return acc;
  }, {});

  const resolveEndpoint = (endpoint) => {
    if (typeof endpoint === 'number' && Number.isInteger(endpoint)) {
      return endpoint;
    }

    if (typeof endpoint === 'string') {
      if (nodeNameToIndex[endpoint] !== undefined) {
        return nodeNameToIndex[endpoint];
      }

      const aliasName = endpointAliases[endpoint];
      return aliasName !== undefined ? nodeNameToIndex[aliasName] : undefined;
    }

    if (endpoint && typeof endpoint === 'object' && typeof endpoint.name === 'string') {
      return nodeNameToIndex[endpoint.name];
    }

    return undefined;
  };

  const normalizedLinks = Array.isArray(data?.links)
    ? data.links
      .map((link) => {
        const source = resolveEndpoint(link?.source);
        const target = resolveEndpoint(link?.target);
        return {
          ...link,
          source,
          target,
          value: Number(link?.value),
        };
      })
      .filter(
        (link) =>
          Number.isFinite(link.value) &&
          link.value > 0 &&
          Number.isInteger(link.source) &&
          Number.isInteger(link.target) &&
          link.source >= 0 &&
          link.target >= 0 &&
          link.source < renderNodes.length &&
          link.target < renderNodes.length
      )
    : [];

  const normalizedData = {
    ...data,
    nodes: renderNodes,
    links: normalizedLinks,
  };

  const labelHeaders = Array.from(new Set(renderNodes.map((node) => node.name).filter(Boolean)));

  const nodeValueLookup = renderNodes.reduce((acc, node) => {
    acc[node.name] = {
      signedValue: node.signedValue,
      formattedValue: node.formattedValue,
    };
    return acc;
  }, {});

  const useSignedCashFlowColors = typeof title === 'string' && title.startsWith('Cash Flow');

  if (!data || normalizedNodes.length < 2 || normalizedLinks.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%" flexDirection="column">
        <Typography variant="body1" color="textSecondary">
          {loading ? "Loading diagram..." : "No data available"}
        </Typography>
        {loading && <CircularProgress sx={{ mt: 2 }} />}
      </Box>
    );
  }

  return (
    <Box position="relative" height="100%">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
          {title}
        </Typography>
        <MuiTooltip title="View component descriptions and sub-components">
          <IconButton
            size="small"
            onClick={() => setInfoDialogOpen(true)}
            sx={{ p: 0, minWidth: '24px', width: '24px', height: '24px' }}
          >
            <InfoOutlinedIcon sx={{ fontSize: '20px' }} />
          </IconButton>
        </MuiTooltip>
      </Box>

      {/* Component Descriptions Dialog */}
      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '80vh',
            backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Sankey Diagram Components</Typography>
          <IconButton
            onClick={() => setInfoDialogOpen(false)}
            size="small"
            sx={{ ml: 1 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ overflowY: 'auto', py: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {labelHeaders.map((label) => {
              const statementKind = statementType === 'cashflow' || title?.includes('Cash Flow') ? 'cashflow' : 'income';
              const componentInfo = getComponentDescription(label, statementKind);
              const likelySubComponents = componentInfo?.subComponents || [
                'See company filings for detailed line items',
                'Typical composition varies by sector and business model'
              ];
              const signedValue = Number(nodeValueLookup?.[label]?.signedValue);
              const sectionLabelColor = Number.isFinite(signedValue)
                ? (signedValue >= 0 ? theme.palette.success.main : theme.palette.error.main)
                : theme.palette.primary.main;

              return (
              <Box key={label} sx={{ pb: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, '&:last-child': { borderBottom: 'none' } }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: sectionLabelColor, mb: 0.5 }}>
                  {label}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', mb: 0.5, color: theme.palette.text.secondary }}>
                  Typical Sub-components:
                </Typography>
                <List sx={{ py: 0, ml: 1 }}>
                  {likelySubComponents.map((subComponent, idx) => (
                    <ListItem key={idx} sx={{ py: 0.25, px: 0, pl: 1 }}>
                      <ListItemText
                        primary={subComponent}
                        primaryTypographyProps={{
                          variant: 'caption',
                          sx: {
                            color: statementKind === 'cashflow'
                              ? getCashFlowSubcomponentColor(subComponent, theme, sectionLabelColor)
                              : getIncomeSubcomponentColor(subComponent, theme, sectionLabelColor),
                          },
                        }}
                        sx={{ my: 0 }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
              );
            })}
          </Box>
        </DialogContent>
      </Dialog>
      
      {loading && (
        <Box 
          position="absolute" 
          top={0} 
          left={0} 
          right={0} 
          bottom={0} 
          display="flex" 
          alignItems="center" 
          justifyContent="center"
          bgcolor={isDarkMode ? 'rgba(11, 18, 32, 0.7)' : 'rgba(255, 255, 255, 0.7)'}
          zIndex={1}
        >
          <CircularProgress />
        </Box>
      )}
      
      {/* Add refresh button */}
      {onRefresh && (
        <Box sx={{ position: 'absolute', top: 0, right: 0, zIndex: 10, m: 1 }}>
          <Button 
            size="small" 
            variant="outlined" 
            onClick={onRefresh} 
            disabled={loading}
            sx={{ minWidth: '30px', width: '30px', height: '30px', p: 0 }}
          >
            <span role="img" aria-label="refresh">🔄</span>
          </Button>
        </Box>
      )}
      
      <Box height="90%" className="sankey-container">
        <ResponsiveContainer width="100%" height="100%">
          <Sankey
            data={normalizedData}
            node={(props) => (
              <CustomNode
                {...props}
                containerWidth={1000}
                labelColor={labelColor}
                currencySymbol={currencySymbol}
                useSignedCashFlowColors={useSignedCashFlowColors}
                nodeValueLookup={nodeValueLookup}
              />
            )}
            link={<CustomLink linkStroke={linkStroke} />}
            margin={{ top: 20, right: 180, bottom: 20, left: 180 }} // Increased margins on left and right
            nodePadding={40}
            nodeWidth={10}
            iterations={64} // More iterations for better layout
            linkCurvature={0.5}
          >
            <Tooltip 
              formatter={(value, name, entry) => {
                const entryPayload = entry?.payload;
                const payloadNodeName = typeof entryPayload?.name === 'string'
                  ? entryPayload.name
                  : undefined;
                const nestedPayloadNodeName = typeof entryPayload?.payload?.name === 'string'
                  ? entryPayload.payload.name
                  : undefined;
                const tooltipName = typeof name === 'string' ? name : undefined;
                const resolvedNodeName = payloadNodeName || nestedPayloadNodeName || tooltipName;

                const lookupSignedValue = typeof resolvedNodeName === 'string'
                  ? nodeValueLookup?.[resolvedNodeName]?.signedValue
                  : undefined;
                const entrySignedValue = Number.isFinite(entryPayload?.signedValue)
                  ? entryPayload.signedValue
                  : entryPayload?.payload?.signedValue;
                const numericFallback = Number(value);
                const tooltipValue = Number.isFinite(lookupSignedValue)
                  ? lookupSignedValue
                  : (Number.isFinite(entrySignedValue)
                    ? entrySignedValue
                    : (Number.isFinite(numericFallback) ? numericFallback : 0));
                const tooltipLabel = resolvedNodeName || name;
                return [formatDisplayValue(tooltipValue, currencySymbol), tooltipLabel];
              }} 
            />
          </Sankey>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default ReactSankeyDiagram;